import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as v from 'valibot';
import { useQueryClient } from '@tanstack/react-query';

import {
  Button,
  type ColumnDef,
  DataTable,
  dialogs,
  Icon,
  Modal,
  Pagination,
  Radio,
  RadioGroup,
  Spinner,
  Text,
} from '@nvision/ui';

import { NoData } from '@/modules/errors';

import { useAddressBookSearch, useDeleteAddress } from '../../../hooks';
import {
  type AddressItem,
  type AddressSearchFormValues,
  type AddressSearchParams,
  addressSearchParamsSchema,
} from '../../../models';
import { CACHE_KEYS } from '../../../types';
import type { SearchFormRef } from '../../products';
import { AddressModal } from '../address-modal';
import { AddressSearchForm } from '../address-search-form';

import styles from './address-search-modal.module.css';

const SORT_COLUMN_TO_API: Record<string, string> = {
  city: 'City',
  country: 'CountryCode',
  postalCode: 'PostalCode',
  state: 'StateCode',
};

const SORT_API_TO_COLUMN: Record<string, string> = {
  City: 'city',
  CountryCode: 'country',
  PostalCode: 'postalCode',
  StateCode: 'state',
};

interface AddressSearchModalProps {
  defaultValues?: Partial<AddressSearchFormValues>;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onSelectionChange?: (value: AddressItem) => void;
}

export const AddressSearchModal: React.FC<AddressSearchModalProps> = ({
  defaultValues,
  isOpen = false,
  setIsOpen,
  onSelectionChange,
}) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation(['translation']);
  const [params, setParams] = useState(v.getDefaults(addressSearchParamsSchema) as unknown as AddressSearchParams);
  const [selectedAddress, setSelectedAddress] = useState<AddressItem | null>(null);
  const [searchCommitted, setSearchCommitted] = useState(false);
  const [isAddAddressOpen, setIsAddAddressOpen] = useState(false);
  const [isEditAddressOpen, setIsEditAddressOpen] = useState(false);
  const [editingAddressId, setEditingAddressId] = useState<string | undefined>(undefined);
  const selectedAddressRef = useRef<AddressItem | null>(null);

  const { data: addresses, isFetching } = useAddressBookSearch(params, {
    enabled: searchCommitted,
    onError: () => {
      setParams((prev) => (prev.offset > 0 ? { ...prev, offset: prev.offset - prev.limit } : prev));
    },
  });
  const searchFormRef = useRef<SearchFormRef>({} as SearchFormRef);
  const { mutate: deleteAddress, isPending: isDeleteAddressLoading } = useDeleteAddress();

  const sortColumnId = params.sorting ? SORT_API_TO_COLUMN[params.sorting] : undefined;
  const tableSorting: { desc: boolean; id: string }[] =
    sortColumnId !== undefined ? [{ desc: false, id: sortColumnId }] : [];

  const reset = () => {
    setSelectedAddress(null);
    setSearchCommitted(false);
    queryClient.removeQueries({ queryKey: [...CACHE_KEYS.all, 'addressBookSearch'] });
    setParams(v.getDefaults(addressSearchParamsSchema) as unknown as AddressSearchParams);
  };

  // TODO: temporary solution, need to implement when BE will be ready
  const handleSortingChange = (newSorting: { desc: boolean; id: string }[]) => {
    setParams((prev) => {
      if (newSorting.length === 0) {
        return { ...prev, sorting: undefined };
      }
      const [sort] = newSorting;
      if (!sort) {
        return { ...prev, sorting: undefined };
      }

      if (sort.desc) {
        return { ...prev, sorting: undefined };
      }
      const apiKey = SORT_COLUMN_TO_API[sort.id];
      if (!apiKey) {
        return prev;
      }

      return { ...prev, sorting: apiKey };
    });
  };

  const close = () => {
    setSearchCommitted(false);
    searchFormRef.current?.reset?.();
    setSelectedAddress(null);
    setIsAddAddressOpen(false);
    setIsEditAddressOpen(false);
    setEditingAddressId(undefined);
    queryClient.removeQueries({ queryKey: [...CACHE_KEYS.all, 'addressBookSearch'] });
    setIsOpen(false);
  };

  const submit = (values: AddressSearchFormValues) => {
    setSearchCommitted(true);
    setParams((prev) => ({
      ...prev,
      address1: values.address1,
      address2: values.address2,
      city: values.city,
      country: values.country,
      name: values.name,
      offset: 0, // Reset to first page on new search
      postalCode: values.postalCode,
      sorting: undefined,
      state: values.state,
    }));
  };

  const handleSubmitSelection = () => {
    if (selectedAddress && onSelectionChange) {
      onSelectionChange(selectedAddress);
      close();
    }
  };

  const handleDeleteActionClick = (row: AddressItem) => {
    if (isDeleteAddressLoading) {
      return;
    }

    dialogs.warning({
      actions: [
        {
          label: t('translation:default.buttons.delete'),
          onPress: () => deleteAddress(row.id),
          variant: 'destructive',
        },
        {
          label: t('translation:default.buttons.cancel'),
        },
      ],
      content: t('translation:default.buttons.deleteAddress'),
      title: t('translation:default.buttons.warning'),
    });
  };

  const handleAddAddressCreated = (createdAddressId: string) => {
    setIsAddAddressOpen(false);
    setEditingAddressId(createdAddressId);
    setIsEditAddressOpen(true);
  };

  useEffect(() => {
    selectedAddressRef.current = selectedAddress || null;
  }, [selectedAddress]);

  const tableColumns: ColumnDef<AddressItem>[] = useMemo(
    () => [
      {
        accessorKey: 'id',
        cell: (props) => {
          const row = props.row.original;
          const isSelected = selectedAddressRef.current?.id === row.id;

          return (
            <RadioGroup
              value={isSelected ? row.id : ''}
              onChange={() => {
                selectedAddressRef.current = row;
                setSelectedAddress(row);
              }}
              className={styles.radio}
            >
              <Radio value={row.id} className={styles.radio} />
            </RadioGroup>
          );
        },
        enableSorting: false,
        header: '',
        maxSize: 0,
      },
      {
        accessorKey: 'country',
        cell: (props) => props.row.original.country,
        header: 'Country',
        sortingFn: () => 0,
      },
      {
        accessorKey: 'postalCode',
        cell: (props) => props.row.original.postalCode,
        header: 'Postal Code',
        sortingFn: () => 0,
      },
      {
        accessorKey: 'city',
        cell: (props) => props.row.original.city,
        header: 'City',
        sortingFn: () => 0,
      },
      {
        accessorKey: 'state',
        cell: (props) => props.row.original.state,
        header: 'State',
        sortingFn: () => 0,
      },
      {
        accessorKey: 'name',
        cell: (props) => props.row.original.name ?? '',
        header: 'Company Name',
        sortingFn: () => 0,
      },
      {
        accessorKey: 'address1',
        cell: (props) => props.row.original.address1 ?? '',
        header: 'Address 1',
        sortingFn: () => 0,
      },
      {
        accessorKey: 'address2',
        cell: (props) => props.row.original.address2 ?? '',
        header: 'Address 2',
        sortingFn: () => 0,
      },
    ],
    [],
  );

  return (
    <>
      <Modal isOpen={isOpen} onOpenChange={(value) => !value && close()} className={styles.modal}>
        <Modal.Header className={styles.header}>
          <Text size="xlg" weight="semibold">
            Address Search
          </Text>
          <Icon name="x" onClick={close} size={20} cursor="pointer" />
        </Modal.Header>
        <Modal.Content className={styles.content}>
          <AddressSearchForm
            defaultValues={defaultValues}
            formRef={searchFormRef}
            handleReset={reset}
            handleSubmit={submit}
          />
          {isFetching || isDeleteAddressLoading ? (
            <div className={styles.loadingContainer}>
              <Spinner size="lg" />
            </div>
          ) : addresses?.data.length ? (
            <div className={styles.resultsSection}>
              <Text size="sm">
                {addresses.total === 1 ? (
                  <>
                    <Text size="sm" weight="bold">{`${addresses.total} address `}</Text>is found
                  </>
                ) : (
                  <>
                    <Text size="sm" weight="bold">{`${addresses.total} addresses `}</Text>are found
                  </>
                )}
              </Text>
              <div className={styles.tableContainer}>
                <DataTable<AddressItem>
                  columns={tableColumns}
                  actions={[
                    {
                      icon: 'square-pen',
                      id: 'edit',
                      onClick: (row) => {
                        setSelectedAddress(row);
                        setEditingAddressId(row.id);
                        setIsEditAddressOpen(true);
                      },
                      variant: 'primary',
                    },
                    {
                      icon: 'trash',
                      id: 'delete',
                      isDisabled: () => isDeleteAddressLoading,
                      onClick: handleDeleteActionClick,
                      variant: 'destructive',
                    },
                  ]}
                  data={addresses?.data}
                  isLoading={isFetching || isDeleteAddressLoading}
                  page={params.offset / params.limit + 1}
                  limit={params.limit}
                  totalItems={addresses.total}
                  sorting={tableSorting}
                  onSortingChange={handleSortingChange}
                />
              </div>

              <Pagination
                className={styles.pagination}
                totalItems={addresses.total}
                limit={params.limit}
                page={params.offset / params.limit + 1}
                onPageChange={(page: number) => {
                  setParams((prev) => ({ ...prev, offset: (page - 1) * prev.limit }));
                }}
                onOffsetChange={(offset: number) => setParams((prev) => ({ ...prev, offset }))}
                onLimitChange={(limit: number) => setParams((prev) => ({ ...prev, limit, offset: 0 }))}
              />
            </div>
          ) : (
            <NoData className={styles.noData} description="No data to display." />
          )}
        </Modal.Content>
        <Modal.Footer className={styles.footer}>
          <Button iconLeft="plus-circle" size="md" onClick={() => setIsAddAddressOpen(true)}>
            Add new address
          </Button>
          <div className={styles.footerActions}>
            <Button type="button" onClick={close} outline secondary>
              Cancel
            </Button>
            <Button isDisabled={!selectedAddress} onClick={handleSubmitSelection}>
              Submit
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
      <AddressModal
        mode="add"
        isOpen={isAddAddressOpen}
        onClose={() => setIsAddAddressOpen(false)}
        onCreated={handleAddAddressCreated}
      />

      <AddressModal
        mode="edit"
        addressId={editingAddressId}
        isOpen={isEditAddressOpen}
        onClose={() => {
          setIsEditAddressOpen(false);
          setEditingAddressId(undefined);
        }}
      />
    </>
  );
};
